import { useEffect, useMemo, useState } from "react";
import { api, formatDate } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";
import { PhoneCell, EmailCell } from "@/components/GuestContactCells";

const initial = { first_name:"", last_name:"", company:"", email:"", phone:"", street:"", postal_code:"", city:"", country:"Deutschland", language:"Deutsch", notes:"", billing_company:"", billing_attention:"", billing_name:"", billing_address:"", gdpr_consent:true, newsletter_consent:false };

export default function Guests() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [fLang, setFLang] = useState("");
  const [fBilling, setFBilling] = useState("");
  const [fCompany, setFCompany] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initial);
  const nav = useNavigate();

  const load = () => api.get("/guests").then(r => setRows(r.data));
  useEffect(() => { load(); }, []);

  // Optimistisches Update nach Inline-Kontakt-Änderung
  const updateGuest = (updated) => {
    setRows(prev => prev.map(g => g.id === updated.id ? { ...g, ...updated } : g));
  };

  // Live-Filter (client-seitig — sofort, kein Netz-Roundtrip)
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return rows.filter(g => {
      if (s) {
        const bag = [
          g.first_name, g.last_name, g.company, g.email, g.phone,
          g.city, g.postal_code, g.notes,
          g.billing_company, g.billing_name, g.billing_address, g.billing_attention,
        ].filter(Boolean).join(" ").toLowerCase();
        if (!bag.includes(s)) return false;
      }
      if (fLang && (g.language || "") !== fLang) return false;
      if (fBilling === "with"    && !(g.billing_company || g.billing_address || g.billing_name)) return false;
      if (fBilling === "without" &&  (g.billing_company || g.billing_address || g.billing_name)) return false;
      if (fCompany === "with"    && !(g.company || "").trim()) return false;
      if (fCompany === "without" &&  (g.company || "").trim()) return false;
      return true;
    });
  }, [rows, q, fLang, fBilling, fCompany]);

  const activeFilterCount = (q ? 1 : 0) + (fLang ? 1 : 0) + (fBilling ? 1 : 0) + (fCompany ? 1 : 0);
  const resetFilters = () => { setQ(""); setFLang(""); setFBilling(""); setFCompany(""); };
  const languages = useMemo(() => {
    return Array.from(new Set(rows.map(g => g.language).filter(Boolean))).sort();
  }, [rows]);

  const save = async () => {
    try {
      await api.post("/guests", form);
      toast.success("Gast angelegt");
      setForm(initial); setOpen(false); load();
    } catch (e) { toast.error("Fehler: " + (e.response?.data?.detail || e.message)); }
  };

  const F = (k, label, type="text") => (
    <div><Label>{label}</Label>
      <Input value={form[k]} onChange={e=>setForm({...form,[k]:e.target.value})} className="mt-1.5 rounded-xl" type={type} data-testid={`guest-${k}-input`}/></div>
  );

  return (
    <div className="space-y-6" data-testid="guests-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold" style={{fontFamily:"'Work Sans',sans-serif"}}>Gäste</h1>
          <p className="text-muted-foreground mt-1">
            {activeFilterCount > 0
              ? <>{filtered.length} von {rows.length} Gästen · <button onClick={resetFilters} className="underline hover:text-primary" data-testid="guests-filter-reset">Filter zurücksetzen</button></>
              : <>{rows.length} Gäste in der Datenbank</>}
          </p>
        </div>
        <div className="flex gap-3">
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-guest-btn" className="rounded-xl bg-primary"><Plus size={16} weight="bold"/> <span className="ml-1.5">Neuer Gast</span></Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle>Neuen Gast anlegen</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-4">
                {F("first_name","Vorname")}{F("last_name","Nachname")}
                {F("company","Firma")}{F("email","E-Mail","email")}
                {F("phone","Telefon")}{F("language","Sprache")}
                {F("street","Straße")}{F("postal_code","PLZ")}
                {F("city","Ort")}{F("country","Land")}
              </div>
              <fieldset className="border border-border rounded-xl p-3 space-y-2.5 mt-2">
                <legend className="px-2 text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">
                  Rechnungsadresse (optional)
                </legend>
                <div className="text-[11px] text-muted-foreground -mt-1">
                  Nur ausfüllen, wenn die Rechnung an eine andere Firma/Adresse geht.
                  Wird bei jeder Rechnung automatisch als Empfänger vorbelegt.
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Rechnungs-Firma</Label>
                    <Input value={form.billing_company || ""} onChange={e=>setForm({...form, billing_company:e.target.value})}
                           className="rounded-xl mt-1.5" data-testid="new-guest-billing-company"/>
                  </div>
                  <div>
                    <Label>z.Hd. (Ansprechpartner)</Label>
                    <Input value={form.billing_attention || ""} onChange={e=>setForm({...form, billing_attention:e.target.value})}
                           className="rounded-xl mt-1.5" data-testid="new-guest-billing-attention"/>
                  </div>
                  <div className="col-span-2">
                    <Label>Name auf Rechnung</Label>
                    <Input value={form.billing_name || ""} onChange={e=>setForm({...form, billing_name:e.target.value})}
                           placeholder="Leer = Vor- + Nachname von oben" className="rounded-xl mt-1.5" data-testid="new-guest-billing-name"/>
                  </div>
                  <div className="col-span-2">
                    <Label>Rechnungs-Adresse (Straße, PLZ Ort, Land)</Label>
                    <Input value={form.billing_address || ""} onChange={e=>setForm({...form, billing_address:e.target.value})}
                           className="rounded-xl mt-1.5" data-testid="new-guest-billing-address"/>
                  </div>
                </div>
              </fieldset>
              <label className="flex items-center gap-2 mt-2 text-sm">
                <Checkbox checked={form.newsletter_consent} onCheckedChange={(v)=>setForm({...form,newsletter_consent:!!v})} data-testid="guest-newsletter-checkbox"/>
                Newsletter-Einwilligung
              </label>
              <div className="flex justify-end gap-2 mt-4">
                <Button variant="outline" onClick={()=>setOpen(false)} className="rounded-xl">Abbrechen</Button>
                <Button onClick={save} className="rounded-xl bg-primary" data-testid="save-guest-btn">Speichern</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Prominente Live-Suche + Filter */}
      <div className="msb-card p-3 space-y-3">
        <div className="relative">
          <MagnifyingGlass className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={20}/>
          <Input
            placeholder="Nach Name, E-Mail, Firma, Telefon, Ort, Notizen suchen …"
            value={q}
            onChange={e=>setQ(e.target.value)}
            className="pl-12 pr-24 rounded-xl h-12 text-base"
            data-testid="guest-search-input"
            autoFocus/>
          {q && (
            <button
              type="button"
              onClick={()=>setQ("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-muted-foreground hover:text-primary px-2 py-1"
              data-testid="guest-search-clear">
              Zurücksetzen
            </button>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <select value={fLang} onChange={e=>setFLang(e.target.value)}
                  className="h-8 rounded-lg border border-slate-300 bg-white px-2" data-testid="guests-filter-lang">
            <option value="">Alle Sprachen</option>
            {languages.map(l => <option key={l} value={l}>{l}</option>)}
          </select>
          <select value={fCompany} onChange={e=>setFCompany(e.target.value)}
                  className="h-8 rounded-lg border border-slate-300 bg-white px-2" data-testid="guests-filter-company">
            <option value="">Alle Gäste</option>
            <option value="with">Nur mit Firma</option>
            <option value="without">Nur Privatgäste</option>
          </select>
          <select value={fBilling} onChange={e=>setFBilling(e.target.value)}
                  className="h-8 rounded-lg border border-slate-300 bg-white px-2" data-testid="guests-filter-billing">
            <option value="">Rechnungsadresse: egal</option>
            <option value="with">Mit Rechnungsadresse</option>
            <option value="without">Ohne Rechnungsadresse</option>
          </select>
          {activeFilterCount > 0 && (
            <button onClick={resetFilters} className="h-8 px-3 rounded-lg text-xs font-semibold text-rose-600 hover:bg-rose-50" data-testid="guests-clear-all">
              Alle Filter löschen
            </button>
          )}
        </div>
      </div>

      <div className="msb-card p-0 overflow-hidden">
        <table className="w-full">
          <thead className="bg-muted/60">
            <tr className="text-xs uppercase tracking-widest text-muted-foreground">
              <th className="text-left p-4">Name</th>
              <th className="text-left p-4">Telefon</th>
              <th className="text-left p-4">E-Mail</th>
              <th className="text-left p-4 whitespace-nowrap">Letzte Buchung ▼</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(g => (
              <tr key={g.id} onClick={()=>nav(`/gaeste/${g.id}`)} data-testid={`guest-row-${g.id}`}
                  className="border-t border-border hover:bg-muted/40 cursor-pointer transition-colors">
                <td className="p-4">
                  <div className="font-medium">{g.first_name} {g.last_name}</div>
                  {g.company && <div className="text-xs text-muted-foreground">{g.company}</div>}
                </td>
                <td className="p-4 text-sm whitespace-nowrap">
                  <PhoneCell guest={g} onGuestUpdated={updateGuest}/>
                </td>
                <td className="p-4 text-sm">
                  <EmailCell guest={g} onGuestUpdated={updateGuest}/>
                </td>
                <td className="p-4 text-sm whitespace-nowrap font-medium">
                  {g.last_booking
                    ? formatDate(g.last_booking)
                    : <span className="text-muted-foreground italic">—</span>}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={4} className="p-8 text-center text-muted-foreground">
                {q ? <>Keine Treffer für „<strong>{q}</strong>". Andere Schreibweise?</> : "Keine Gäste in der Datenbank."}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
